import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import ReusableTable from "../components/Table/ReusableTable";
import Button from "../components/Button/Button";
import {
  Building2,
  CheckCircle2,
  Download,
  Eye,
  Layers3,
  Mail,
  X,
  XCircle,
} from "lucide-react";
import { get, post } from "../utils/api";
import { toast } from "react-toastify";

const cardPalette = [
  {
    halo: "from-[#4338ca]/18 via-[#8b5cf6]/16 to-[#ec4899]/12",
    border: "border-[#d9d6ff]",
    chip: "bg-[#eef2ff] text-[#4338ca]",
    accent: "bg-gradient-to-br from-[#4f46e5] via-[#7c3aed] to-[#db2777]",
  },
  {
    halo: "from-[#0f766e]/18 via-[#14b8a6]/14 to-[#22c55e]/10",
    border: "border-[#c7f9ef]",
    chip: "bg-[#ecfeff] text-[#0f766e]",
    accent: "bg-gradient-to-br from-[#115e59] via-[#0f766e] to-[#10b981]",
  },
  {
    halo: "from-[#9a3412]/18 via-[#f97316]/15 to-[#f59e0b]/10",
    border: "border-[#fed7aa]",
    chip: "bg-[#fff7ed] text-[#c2410c]",
    accent: "bg-gradient-to-br from-[#c2410c] via-[#ea580c] to-[#f59e0b]",
  },
];

const normalizeStatus = (status) => {
  if (!status) return "Pending";
  return status;
};

const DECK_DISMISS_THRESHOLD = 110;
const SWIPE_OUT_DISTANCE = 560;

const buildSummaryFunds = (products = []) => {
  const grouped = products.reduce((acc, product) => {
    const key = `${product.fund_name || "Unknown Fund"}__${product.class_code || "-"}`;
    if (!acc[key]) {
      acc[key] = {
        fund_name: product.fund_name || "Unknown Fund",
        class_code: product.class_code || "-",
      };
    }
    return acc;
  }, {});

  return Object.values(grouped).sort((a, b) => {
    const classA = a.class_code || "";
    const classB = b.class_code || "";
    const classCompare = classA.localeCompare(classB, undefined, { numeric: true, sensitivity: "base" });
    if (classCompare !== 0) return classCompare;
    const fundA = a.fund_name || "";
    const fundB = b.fund_name || "";
    return fundA.localeCompare(fundB, undefined, { numeric: true, sensitivity: "base" });
  });
};

// ProductTypeCards component is removed since we now use ReusableTable to display fund commercial details in a tabular layout.

const SalesHeadDashboard = () => {
  const location = useLocation();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deckIndex, setDeckIndex] = useState(0);
  const [detailCache, setDetailCache] = useState({});
  const [detailLoadingSeq, setDetailLoadingSeq] = useState(null);
  const [dragOffset, setDragOffset] = useState(0);
  const [isDraggingDeck, setIsDraggingDeck] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedDistributor, setSelectedDistributor] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);

  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [selectedActionDistributor, setSelectedActionDistributor] = useState(null);
  const dragStartXRef = useRef(null);
  const dragPointerIdRef = useRef(null);
  const swipeTimeoutRef = useRef(null);

  const [successModal, setSuccessModal] = useState({
    isOpen: false,
    message: "",
    type: "success",
  });
  const [emailActionHandled, setEmailActionHandled] = useState("");

  const targetDistributorSequence = useMemo(
    () => new URLSearchParams(location.search).get("distributor_sequence_number") || "",
    [location.search]
  );
  const requestedEmailAction = useMemo(
    () => (new URLSearchParams(location.search).get("action") || "").toLowerCase(),
    [location.search]
  );

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await get("/sales_listing/fetchDistributorSalesHead");

      if (response.data?.Success) {
        const mappedData = (response.data.Data || [])
          .map((item) => {
            const uniqueFunds = buildSummaryFunds(item.products || []);
            const uniqueClasses = [
              ...new Set((item.products || []).map((product) => product.class_code).filter(Boolean)),
            ].sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" }));

            return {
              distributor_name: item.distributor_name || "-",
              arn_code: item.arn_code || "-",
              distributor_email: item.distributor_email || "-",
              pan: item.pan_number || "-",
              city: item.city || "-",
              distributor_sequence_number: item.distributor_sequence_number,
              sales_head_status: normalizeStatus(item.sales_head_status),
              fundName: uniqueFunds.map((fund) => fund.fund_name).join(", ") || "-",
              class: uniqueClasses.join(", ") || "-",
              funds: uniqueFunds,
              products: item.products || [],
            };
          })
          .sort((a, b) => {
            if (a.class === "-" && b.class === "-") return 0;
            if (a.class === "-") return 1;
            if (b.class === "-") return -1;
            return a.class.localeCompare(b.class, undefined, { numeric: true, sensitivity: "base" });
          });

        setData(mappedData);
        setDeckIndex(0);
        setDragOffset(0);
        setIsDraggingDeck(false);
      } else {
        if (response.data?.Message !== "No Records Found") {
          toast.error(response.data?.Message || "Failed to fetch data");
        }
        setData([]);
        setDeckIndex(0);
      }
    } catch (error) {
      console.error("Fetch error:", error);
      toast.error("Error fetching sales head data");
      setData([]);
      setDeckIndex(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchDistributorDetails = async (sequenceNumber, force = false) => {
    if (!sequenceNumber) return [];
    if (!force && detailCache[sequenceNumber]) {
      return detailCache[sequenceNumber];
    }

    try {
      setDetailLoadingSeq(sequenceNumber);
      const response = await post("/sales_listing/fetchDistributorWithProducts", {
        distributor_sequence_number: sequenceNumber,
      });

      if (response.data?.Success) {
        const details = (response.data.Data || []).sort((a, b) => {
          const classA = a.class_code || "";
          const classB = b.class_code || "";
          const classCompare = classA.localeCompare(classB, undefined, { numeric: true, sensitivity: "base" });
          if (classCompare !== 0) return classCompare;
          const fundA = a.fund_code || a.fund_name || "";
          const fundB = b.fund_code || b.fund_name || "";
          return fundA.localeCompare(fundB, undefined, { numeric: true, sensitivity: "base" });
        });
        setDetailCache((prev) => ({
          ...prev,
          [sequenceNumber]: details,
        }));
        return details;
      }

      toast.error(response.data?.Message || "Failed to fetch details.");
      return [];
    } catch (error) {
      console.error("Detail fetch error:", error);
      toast.error("Error fetching details.");
      return [];
    } finally {
      setDetailLoadingSeq((prev) => (prev === sequenceNumber ? null : prev));
    }
  };

  const activeCard = data[deckIndex] || null;
  const deckCompleted = !loading && data.length > 0 && deckIndex >= data.length;
  const visibleCards = deckCompleted ? [] : data.slice(deckIndex, deckIndex + 3);

  useEffect(() => {
    if (activeCard?.distributor_sequence_number) {
      fetchDistributorDetails(activeCard.distributor_sequence_number);
    }
  }, [activeCard?.distributor_sequence_number]);

  useEffect(() => {
    setEmailActionHandled("");
  }, [location.search]);

  useEffect(() => {
    if (!targetDistributorSequence || data.length === 0) return;

    const targetIndex = data.findIndex(
      (item) => item.distributor_sequence_number === targetDistributorSequence
    );

    if (targetIndex >= 0 && targetIndex !== deckIndex) {
      setDeckIndex(targetIndex);
    }
  }, [data, targetDistributorSequence, deckIndex]);

  const pendingCount = data.length;
  const totalFundCount = useMemo(
    () => data.reduce((sum, item) => sum + item.funds.length, 0),
    [data]
  );

  useEffect(() => {
    return () => {
      if (swipeTimeoutRef.current) {
        clearTimeout(swipeTimeoutRef.current);
      }
    };
  }, []);

  const resetDeckDrag = () => {
    dragStartXRef.current = null;
    dragPointerIdRef.current = null;
    setDragOffset(0);
    setIsDraggingDeck(false);
  };

  const moveDeckForward = (direction) => {
    setIsDraggingDeck(false);
    setDragOffset(direction * SWIPE_OUT_DISTANCE);

    if (swipeTimeoutRef.current) {
      clearTimeout(swipeTimeoutRef.current);
    }

    swipeTimeoutRef.current = setTimeout(() => {
      setDeckIndex((prev) => Math.min(prev + 1, data.length));
      dragStartXRef.current = null;
      dragPointerIdRef.current = null;
      setDragOffset(0);
    }, 220);
  };

  const handleDeckPointerDown = (event) => {
    if (!activeCard || event.target.closest("button, a, textarea, input")) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    dragStartXRef.current = event.clientX;
    dragPointerIdRef.current = event.pointerId;
    setIsDraggingDeck(true);
  };

  const handleDeckPointerMove = (event) => {
    if (!isDraggingDeck || dragPointerIdRef.current !== event.pointerId) return;
    setDragOffset(event.clientX - dragStartXRef.current);
  };

  const handleDeckPointerUp = (event) => {
    if (!isDraggingDeck || dragPointerIdRef.current !== event.pointerId) return;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    const travelled = event.clientX - dragStartXRef.current;

    if (Math.abs(travelled) >= DECK_DISMISS_THRESHOLD) {
      moveDeckForward(travelled > 0 ? 1 : -1);
      return;
    }

    resetDeckDrag();
  };

  const handleApprove = async (distributor) => {
    if (!distributor?.distributor_sequence_number) return;

    try {
      const response = await post("/sales_listing/ApproveRejectSalesHead", {
        distributor_sequence_number: distributor.distributor_sequence_number,
        status: "Approved",
        reason: null,
      });

      if (response.data?.Success) {
        setSuccessModal({
          isOpen: true,
          message: "Distributor Agreement has been Approved successfully.",
          type: "success",
        });
        setIsModalOpen(false);
        await fetchData();
      } else {
        toast.error(response.data?.Message || "Approval failed");
      }
    } catch (error) {
      console.error("Approval error:", error);
      toast.error("Error approving distributor");
    }
  };

  const handleRejectInit = (distributor) => {
    setSelectedActionDistributor(distributor);
    setIsRejectModalOpen(true);
    setRejectionReason("");
  };

  const handleRejectSubmit = async () => {
    if (!selectedActionDistributor?.distributor_sequence_number) return;
    if (!rejectionReason.trim()) {
      toast.warn("Please enter a rejection reason");
      return;
    }

    try {
      const response = await post("/sales_listing/ApproveRejectSalesHead", {
        distributor_sequence_number: selectedActionDistributor.distributor_sequence_number,
        status: "Rejected",
        reason: rejectionReason.trim(),
      });

      if (response.data?.Success) {
        setSuccessModal({
          isOpen: true,
          message: "Commercials has been Rejected.",
          type: "success",
        });
        setIsRejectModalOpen(false);
        setIsModalOpen(false);
        setSelectedActionDistributor(null);
        await fetchData();
      } else {
        toast.error(response.data?.Message || "Rejection failed");
      }
    } catch (error) {
      console.error("Rejection error:", error);
      toast.error("Error rejecting distributor");
    }
  };

  const handleView = async (row) => {
    setIsModalOpen(true);
    setModalLoading(true);
    setSelectedDistributor(null);
    setSelectedActionDistributor(row);

    const details = await fetchDistributorDetails(row.distributor_sequence_number, true);
    setSelectedDistributor(details);
    setModalLoading(false);
  };

  useEffect(() => {
    if (!targetDistributorSequence || !requestedEmailAction || data.length === 0) return;
    if (emailActionHandled === `${targetDistributorSequence}:${requestedEmailAction}`) return;

    const targetDistributor = data.find(
      (item) => item.distributor_sequence_number === targetDistributorSequence
    );

    if (!targetDistributor) return;

    handleView(targetDistributor);
    setEmailActionHandled(`${targetDistributorSequence}:${requestedEmailAction}`);

    if (requestedEmailAction === "reject") {
      toast.info("Review the mapped fund details, then use Reject to submit the rejection reason.");
    } else if (requestedEmailAction === "approve") {
      toast.info("Review the mapped fund details, then use Approve to complete the decision.");
    }
  }, [data, targetDistributorSequence, requestedEmailAction, emailActionHandled]);

  const handleDownload = async (distributor) => {
    try {
      if (!distributor?.distributor_sequence_number) {
        toast.error("Distributor Sequence Number missing for this entry.");
        return;
      }

      toast.info("Generating and downloading agreement...");

      const response = await post("/fileGeneration/downloadGeneratedAgreement", {
        distributor_sequence_number: distributor.distributor_sequence_number,
      });

      if (response.data?.Success && response.data.Data) {
        const byteCharacters = atob(response.data.Data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i += 1) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: "application/pdf" });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `Agreement_${distributor.distributor_name || "Document"}.pdf`
        );
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
        window.URL.revokeObjectURL(url);

        toast.success("Download started successfully");
      } else {
        toast.error(response.data?.Message || "Failed to generate agreement");
      }
    } catch (error) {
      console.error("Download failed:", error);
      toast.error(error.response?.data?.Message || "Failed to download agreement");
    }
  };

  const columns = useMemo(
    () => [
      {
        Header: "Distributor Name",
        accessor: "distributor_name",
        size: 200,
        Cell: ({ value }) => <div className="font-medium text-gray-900">{value || "-"}</div>,
      },
      {
        Header: "APRN Code",
        accessor: "arn_code",
        size: 150,
      },
      {
        Header: "PAN Number",
        accessor: "pan",
        size: 150,
      },
      {
        Header: "Fund Name",
        accessor: "fundName",
        size: 250,
        Cell: ({ value }) => (
          <div className="whitespace-normal break-words" title={value}>
            {value}
          </div>
        ),
      },
      {
        Header: "Class",
        accessor: "class",
        size: 150,
        Cell: ({ value }) => (
          <div className="truncate" title={value}>
            {value}
          </div>
        ),
      },
      {
        Header: "Status",
        accessor: "sales_head_status",
        size: 150,
        Cell: ({ value }) => value || "Pending",
      },
      {
        Header: "ACTION",
        accessor: "action",
        size: 250,
        Cell: ({ row }) => (
          <div className="flex justify-center items-center gap-3">
            <button
              onClick={() => handleView(row.original)}
              className="bg-[#F1F4F9] border border-[#E2E8F0] text-gray-800 px-4 py-1.5 rounded-xl transition-all flex items-center gap-2 hover:bg-gray-200 text-sm font-semibold"
              title="View Details"
            >
              <Eye size={18} />
              View
            </button>
            <button
              onClick={() => handleDownload(row.original)}
              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 transition-colors p-1.5 rounded-full"
              title="Download Agreement"
            >
              <Download size={20} />
            </button>
          </div>
        ),
      },
    ],
    []
  );

  const renderDetailedSections = (rows, options = {}) => {
    const { hidePagination = false, compact = false } = options;

    if (!rows || rows.length === 0) {
      return (
        <div className="flex h-full min-h-[160px] items-center justify-center rounded-[20px] border border-dashed border-[#d9dcff] bg-white/70 px-6 text-center text-sm text-[#667085]">
          No fund details found.
        </div>
      );
    }

    const isProduct2 = rows.some(
      (row) =>
        Number(row.product_type_id) === 2 ||
        Number(row.product_type_id) === 3 ||
        (row.management_fee_sharing_per_morethan15cr !== null &&
          row.management_fee_sharing_per_morethan15cr !== undefined &&
          row.management_fee_sharing_per_morethan15cr !== "")
    );

    const commercialTableData = (() => {
      let result = [];
      if (!isProduct2) {
        result = [...rows];
      } else {
        rows.forEach((row) => {
          const hasLess =
            row.management_fee_sharing_per !== null &&
            row.management_fee_sharing_per !== undefined &&
            row.management_fee_sharing_per !== "";
          const hasMore =
            row.management_fee_sharing_per_morethan15cr !== null &&
            row.management_fee_sharing_per_morethan15cr !== undefined &&
            row.management_fee_sharing_per_morethan15cr !== "";

          if (hasLess) {
            result.push({
              ...row,
              _capital_commitment: "Less than 15 Cr",
              _mgmt_fee: row.management_fee_sharing_per,
            });
          }
          if (hasMore) {
            result.push({
              ...row,
              _capital_commitment: "More than 15 Cr",
              _mgmt_fee: row.management_fee_sharing_per_morethan15cr,
            });
          }
          if (!hasLess && !hasMore) {
            result.push({
              ...row,
              _capital_commitment: "-",
              _mgmt_fee: "-",
            });
          }
        });
        if (result.length === 0) result = [...rows];
      }

      return result.sort((a, b) => {
        const classA = a.class_code || "";
        const classB = b.class_code || "";
        const classCompare = classA.localeCompare(classB, undefined, { numeric: true, sensitivity: "base" });
        if (classCompare !== 0) return classCompare;
        const fundA = a.fund_code || a.fund_name || "";
        const fundB = b.fund_code || b.fund_name || "";
        return fundA.localeCompare(fundB, undefined, { numeric: true, sensitivity: "base" });
      });
    })();

    const commercialColumns = [
      ...(isProduct2
        ? [
            {
              Header: "FUND CODE",
              accessor: "fund_code",
              Cell: ({ value, row }) => value || row.original.fund_name || "-",
            },
            { Header: "CLASS CODE", accessor: "class_code" },
            { Header: "CAPITAL COMMITMENT", accessor: "_capital_commitment" },
            {
              Header: "MANAGEMENT FEES P.A.",
              accessor: "_mgmt_fee",
              Cell: ({ value }) =>
                value !== null && value !== undefined && value !== "" && value !== "-"
                  ? `${value}%`
                  : "-",
            },
            {
              Header: "DISTRIBUTOR SHARING IN MONTHLY FEES",
              accessor: "performance_fee_sharing_per",
              Cell: ({ value }) =>
                value !== null && value !== undefined && value !== ""
                  ? `${value}%`
                  : "-",
            },
          ]
        : [
            {
              Header: "FUND CODE",
              accessor: "fund_code",
              Cell: ({ value, row }) => value || row.original.fund_name || "-",
            },
            { Header: "CLASS CODE", accessor: "class_code" },
            { Header: "CONTRIBUTION (IN CR)", accessor: "size" },
            { Header: "M. FEES", accessor: "management_fee_sharing_per" },
            { Header: "TOTAL DISTRIBUTION FEES", accessor: "placement_fee_per" },
            { Header: "UPFRONT", accessor: "upfront_fee_per" },
            { Header: "TRAIL", accessor: "trail_fee_per" },
          ]),
    ];

    if (compact) {
      return (
        <div className="w-full overflow-x-auto rounded-xl border border-[#edf0f7] bg-white shadow-sm">
          <table className="w-full min-w-full table-auto text-center border-collapse">
            <thead className="bg-[#f8fafc] border-b border-gray-100">
              <tr>
                {commercialColumns.map((col, idx) => (
                  <th
                    key={idx}
                    className="px-2.5 py-2 text-[10px] font-extrabold uppercase tracking-wider text-[#4f46e5] text-center"
                  >
                    {typeof col.Header === "string" ? col.Header : col.Header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {commercialTableData.map((rowItem, rIdx) => (
                <tr key={rIdx} className="hover:bg-indigo-50/30 transition-colors">
                  {commercialColumns.map((col, cIdx) => {
                    const rawVal = col.accessor ? rowItem[col.accessor] : undefined;
                    let cellContent = rawVal;

                    if (col.Cell) {
                      cellContent = col.Cell({ value: rawVal, row: { original: rowItem } });
                    } else if (rawVal === null || rawVal === undefined || rawVal === "") {
                      cellContent = "-";
                    }

                    return (
                      <td
                        key={cIdx}
                        className="px-2.5 py-2 text-[11px] font-semibold text-gray-700 text-center whitespace-nowrap"
                      >
                        {cellContent}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    return (
      <div className="overflow-x-auto rounded-xl border border-[#edf0f7] bg-white p-2 shadow-[0_8px_24px_rgba(15,23,42,0.04)]">
        <ReusableTable columns={commercialColumns} data={commercialTableData} hidePagination={hidePagination} />
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top_left,_rgba(129,140,248,0.16),_transparent_32%),radial-gradient(circle_at_top_right,_rgba(244,114,182,0.14),_transparent_28%),linear-gradient(180deg,_#f8f7ff_0%,_#f5f7fb_45%,_#eef3f8_100%)]">
      <div className="max-w-screen-xl mx-auto p-4 space-y-6">
        

        <section className="rounded-[30px] border border-white/70 bg-white/75 px-4 py-5 shadow-[0_18px_60px_rgba(15,23,42,0.08)] backdrop-blur-xl sm:px-6">
          <div className="mb-5 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h2 className="text-xl font-black tracking-tight text-start text-[#20263d]">
                {deckCompleted ? "Sales Dashboard" : "Pending Commercial Approval"}
              </h2>
              <p className="text-xs text-[#667085]">
                {deckCompleted
                  ? "Flash cards are completed. Continue from the dashboard listing below."
                  : "Review complete fund detail headers, then approve or reject directly."}
              </p>
            </div>

            <div />
          </div>

          {loading ? (
            <div className="flex min-h-[640px] items-center justify-center rounded-[28px] border border-dashed border-[#d9dcff] bg-white/70">
              <div className="text-center">
                <div className="mx-auto h-10 w-10 animate-spin rounded-full border-4 border-[#ddd6fe] border-t-[#5b21b6]" />
                <p className="mt-4 text-sm font-semibold text-[#667085]">Loading distributor cards...</p>
              </div>
            </div>
          ) : data.length === 0 ? (
            <div className="flex min-h-[640px] items-center justify-center rounded-[28px] border border-dashed border-[#d9dcff] bg-white/70">
              <div className="max-w-md text-center">
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#ede9fe] text-[#5b21b6]">
                  <Layers3 size={30} />
                </div>
                <h3 className="mt-4 text-xl font-black text-[#1f2340]">No distributors pending review</h3>
                <p className="mt-2 text-sm text-[#667085]">
                  When new distributor requests arrive, they will appear here as action-ready flash cards.
                </p>
              </div>
            </div>
          ) : deckCompleted ? (
            <div className="rounded-[28px] border border-white/80 bg-white/85 p-4 shadow-[0_18px_60px_rgba(15,23,42,0.08)]">
              <div className="border-b border-[#eef2f6] pb-4" />

              <div className="mt-4">
                <ReusableTable columns={columns} data={data} />
              </div>
            </div>
          ) : (
            <div className="relative min-h-[540px] overflow-hidden rounded-[28px] bg-[linear-gradient(135deg,_rgba(79,70,229,0.08),_rgba(255,255,255,0.75)_35%,_rgba(236,72,153,0.06))] px-2 py-4 sm:px-4">
              <div className="relative mx-auto h-[530px] max-w-[920px]">
                {visibleCards.map((item, stackIndex) => {
                  const isActiveCard = stackIndex === 0;
                  const details = detailCache[item.distributor_sequence_number] || [];
                  const loadingDetails =
                    detailLoadingSeq === item.distributor_sequence_number && details.length === 0;
                  const palette = cardPalette[(deckIndex + stackIndex) % cardPalette.length];
                  const baseTranslateX = stackIndex * 22;
                  const baseTranslateY = stackIndex * -12;
                  const baseScale = 1 - stackIndex * 0.035;
                  const baseRotate = stackIndex * 3.5;
                  const activeRotate = dragOffset / 18;
                  const transform = isActiveCard
                    ? `translate(-50%, -50%) translateX(${dragOffset}px) rotate(${activeRotate}deg)`
                    : `translate(-50%, -50%) translateX(${baseTranslateX}px) translateY(${baseTranslateY}px) scale(${baseScale}) rotate(${baseRotate}deg)`;

                  return (
                    <div
                      key={item.distributor_sequence_number}
                      className={`absolute left-1/2 top-1/2 h-[475px] w-[min(95vw,750px)] ${
                        isActiveCard ? "z-30 cursor-grab active:cursor-grabbing" : "pointer-events-none"
                      }`}
                      onPointerDown={isActiveCard ? handleDeckPointerDown : undefined}
                      onPointerMove={isActiveCard ? handleDeckPointerMove : undefined}
                      onPointerUp={isActiveCard ? handleDeckPointerUp : undefined}
                      onPointerCancel={isActiveCard ? resetDeckDrag : undefined}
                      style={{
                        transform,
                        transition: isDraggingDeck && isActiveCard ? "none" : "transform 220ms ease, opacity 220ms ease",
                        opacity: isActiveCard ? 1 : Math.max(0.42, 0.85 - stackIndex * 0.18),
                        touchAction: "pan-y",
                      }}
                    >
                      <div className={`absolute inset-0 rounded-[28px] bg-gradient-to-br ${palette.halo} blur-2xl`} />
                      <div className={`relative h-full overflow-hidden rounded-[28px] border ${palette.border} bg-white shadow-[0_18px_56px_rgba(31,35,64,0.14)]`}>
                        <div className="flex h-full justify-between flex-col rounded-[28px] p-3">
                          <div className={`rounded-[20px] ${palette.accent} px-4 py-1 text-white shadow-lg`}>
                            <div className="flex items-start justify-between gap-3">
                              <div>
                                <p className="text-[10px] font-black uppercase text-white/70">
                                  Distributor Name
                                </p>
                                 <p className="mt-0 text-lg font-black leading-tight">
                                  {item.distributor_name} </p>
                                
                              </div>
                                       <div>
                                <p className="text-[10px] font-black uppercase text-white/70">
                                  City
                                </p>
                                <p className="mt-0 text-lg font-black leading-tight">{item.city}</p>
                              </div>
                              <div className="rounded-2xl bg-white/14 p-2">
                                <Building2 size={16} />
                              </div>
                            </div>

                            <div className="mt-2 grid gap-1 sm:grid-cols-2">
                     
                              {/* <div className="rounded-2xl bg-white/12 px-2 py-1.5">
                                <div className="flex items-start gap-1">
                                  <Mail size={12} className="mt-0.5 shrink-0 text-white/80" />
                                  <div className="min-w-0">
                                    <p className="text-[9px] font-black uppercase tracking-[0.2em] text-white/70">
                                      Email
                                    </p>
                                    <p className="mt-0.5 break-all text-[11px] font-bold leading-snug">
                                      {item.distributor_email}
                                    </p>
                                  </div>
                                </div>
                              </div> */}
                            </div>
                          </div>

                          <div className="mt-2 flex-1 min-h-[220px] max-h-[255px] overflow-y-auto pr-0.5">
                            {loadingDetails ? (
                              <div className="flex h-full min-h-[220px] items-center justify-center rounded-[20px] border border-dashed border-[#d9dcff] bg-white/70">
                                <div className="text-center">
                                  <div className="mx-auto h-8 w-8 animate-spin rounded-full border-4 border-[#ddd6fe] border-t-[#5b21b6]" />
                                  <p className="mt-3 text-sm font-semibold text-[#667085]">
                                    Loading fund details...
                                  </p>
                                </div>
                              </div>
                            ) : (
                              renderDetailedSections(details, { hidePagination: true, compact: true })
                            )}
                          </div>

                          <div className="mt-2 space-y-1.5 pt-0.5">
                            <button
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();
                                handleDownload(item);
                              }}
                              className="flex h-9 w-full items-center justify-center gap-2 rounded-2xl border border-[#dbe4ff] bg-[#f8faff] px-4 text-xs font-bold text-[#3150d0] transition hover:-translate-y-0.5 hover:bg-white"
                            >
                              <Download size={14} />
                              Download Agreement
                            </button>

                            <div className="grid grid-cols-2 gap-1.5">
                              <button
                                type="button"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  handleRejectInit(item);
                                }}
                                className="flex h-9 items-center justify-center gap-2 rounded-2xl bg-[#ef4444] px-4 text-xs font-bold text-white shadow-sm transition hover:-translate-y-0.5 hover:bg-[#dc2626]"
                              >
                                <XCircle size={14} />
                                Reject
                              </button>
                              <button
                                type="button"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  handleApprove(item);
                                }}
                                className="flex h-9 items-center justify-center gap-2 rounded-2xl bg-[#16a34a] px-4 text-xs font-bold text-white shadow-sm transition hover:-translate-y-0.5 hover:bg-[#15803d]"
                              >
                                <CheckCircle2 size={14} />
                                Approve
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </section>

      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-[#0f172a]/45 px-4 py-6 backdrop-blur-sm">
          <div className="w-full max-w-6xl overflow-hidden rounded-[32px] border border-white/70 bg-white shadow-[0_22px_80px_rgba(15,23,42,0.22)]">
            <div className="sticky top-0 z-10 border-b border-[#eef2f6] bg-white/95 backdrop-blur-xl">
              <div className="bg-[linear-gradient(120deg,rgba(99,102,241,0.08),transparent_45%,rgba(236,72,153,0.06))] px-6 py-5">
                <div className="relative">
                  <div className="mx-auto max-w-3xl text-center">
                    <p className="text-[11px] font-black uppercase tracking-[0.26em] text-[#7c3aed]">
                      Distributor Review
                    </p>
                    <h2 className="mt-2 text-2xl font-black text-[#1f2340]">Commercial Details</h2>
                    <p className="mt-2 text-sm text-[#667085]">
                      Review complete distributor information, browse fund mappings with pagination, and take action from one place.
                    </p>
                  </div>
                  <button
                    onClick={() => setIsModalOpen(false)}
                    className="absolute right-0 top-1/2 inline-flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-2xl border border-[#e5e7eb] bg-white text-[#667085] transition hover:bg-[#f8fafc] hover:text-[#dc2626]"
                  >
                    <X size={24} />
                  </button>
                </div>
              </div>
            </div>

            <div className="max-h-[calc(90vh-108px)] overflow-y-auto px-6 py-6">
              {modalLoading ? (
                <div className="flex justify-center py-16">
                  <div className="text-center">
                    <div className="mx-auto h-10 w-10 animate-spin rounded-full border-4 border-[#ddd6fe] border-t-[#5b21b6]" />
                    <p className="mt-4 text-sm font-semibold text-[#667085]">Loading details...</p>
                  </div>
                </div>
              ) : selectedDistributor && selectedDistributor.length > 0 ? (
                <>
                  <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                    <div className="rounded-[24px] border border-[#e9eaff] bg-[#fbfbff] p-4 shadow-sm">
                      <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#98a2b3]">Distributor</p>
                      <p className="mt-2 text-lg font-black text-[#1f2340]">
                        {selectedDistributor[0].distributor_name || "-"}
                      </p>
                      <p className="mt-2 text-sm text-[#667085] break-all">
                        {selectedDistributor[0].distributor_email || "-"}
                      </p>
                    </div>
                    <div className="rounded-[24px] border border-[#e9eaff] bg-[#fbfbff] p-4 shadow-sm">
                      <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#98a2b3]">Identification</p>
                      <p className="mt-2 text-sm font-bold text-[#1f2340]">APRN: {selectedDistributor[0].arn_code || "-"}</p>
                      <p className="mt-1 text-sm font-bold text-[#1f2340]">PAN: {selectedDistributor[0].pan_number || "-"}</p>
                    </div>
                    <div className="rounded-[24px] border border-[#e9eaff] bg-[#fbfbff] p-4 shadow-sm">
                      <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#98a2b3]">Location</p>
                      <p className="mt-2 text-sm font-bold text-[#1f2340]">
                        {[selectedDistributor[0].city, selectedDistributor[0].state].filter(Boolean).join(", ") || "-"}
                      </p>
                      <p className="mt-1 text-sm text-[#667085]">Pin: {selectedDistributor[0].pincode || "-"}</p>
                    </div>
                    <div className="rounded-[24px] border border-[#e9eaff] bg-[#fbfbff] p-4 shadow-sm">
                      <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#98a2b3]">Address</p>
                      <p className="mt-2 text-sm leading-6 text-[#1f2340]">
                        {selectedDistributor[0].address || selectedDistributor[0].distributor_address || "-"}
                      </p>
                    </div>
                  </div>

                  <div className="mt-6 rounded-[28px] border border-[#eef2f6] bg-[#fcfcff] p-4 shadow-sm">
                    <div className="flex flex-col gap-2 border-b border-[#eef2f6] pb-4 sm:flex-row sm:items-end sm:justify-between">
                      <div>
                        <h3 className="text-lg font-black text-[#1f2340]">Fund Details</h3>
                        <p className="text-sm text-[#667085]">
                          Use the section pagination when a product type has more rows than can comfortably fit in one view.
                        </p>
                      </div>
                    </div>

                    <div className="mt-4 space-y-4">
                      {renderDetailedSections(selectedDistributor, { hidePagination: false })}
                    </div>
                  </div>

                  <div className="mt-6 flex flex-col gap-3 border-t border-[#eef2f6] pt-5 sm:flex-row sm:justify-end">
                    <Button
                      variant="danger"
                      onClick={() => handleRejectInit(selectedActionDistributor)}
                      className="rounded-2xl bg-red-600 px-6 py-3 text-white hover:bg-red-700"
                    >
                      Reject
                    </Button>
                    <Button
                      variant="success"
                      onClick={() => handleApprove(selectedActionDistributor)}
                      className="rounded-2xl bg-green-600 px-6 py-3 text-white hover:bg-green-700"
                    >
                      Approve
                    </Button>
                  </div>
                </>
              ) : (
                <div className="py-12 text-center text-red-500">Failed to load details.</div>
              )}
            </div>
          </div>
        </div>
      )}

      {isRejectModalOpen && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-[#111827]/55 px-4 backdrop-blur-sm">
          <div className="w-full max-w-lg rounded-[28px] border border-white/70 bg-white p-6 shadow-[0_18px_60px_rgba(15,23,42,0.18)]">
            <p className="text-[11px] font-black uppercase tracking-[0.28em] text-[#dc2626]">
              Reject Commercials
            </p>
            <h3 className="mt-3 text-2xl font-black text-[#1f2340]">
              {selectedActionDistributor?.distributor_name || "Distributor"}
            </h3>
            <p className="mt-2 text-sm text-[#667085]">
              Add a rejection reason before sending the commercials back in the flow.
            </p>

            <textarea
              className="mt-5 h-36 w-full rounded-[22px] border border-[#fecaca] bg-[#fff7f7] p-4 text-sm text-[#20263d] outline-none transition focus:border-[#ef4444] focus:ring-2 focus:ring-[#fecaca]"
              placeholder="Enter reason for rejection..."
              value={rejectionReason}
              onChange={(event) => setRejectionReason(event.target.value)}
            />

            <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => {
                  setIsRejectModalOpen(false);
                  setSelectedActionDistributor(null);
                }}
                className="rounded-2xl border border-[#e5e7eb] bg-white px-5 py-3 text-sm font-bold text-[#344054] transition hover:bg-[#f9fafb]"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleRejectSubmit}
                className="rounded-2xl bg-[#dc2626] px-5 py-3 text-sm font-bold text-white transition hover:bg-[#b91c1c]"
              >
                Submit Rejection
              </button>
            </div>
          </div>
        </div>
      )}

      {successModal.isOpen && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-[#111827]/55 px-4 backdrop-blur-sm">
          <div className="w-full max-w-sm rounded-[28px] border border-white/70 bg-white p-6 text-center shadow-[0_18px_60px_rgba(15,23,42,0.18)]">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#dcfce7] text-[#15803d]">
              <CheckCircle2 size={30} />
            </div>
            {/* <h3 className="mt-4 text-2xl font-black text-[#1f2340]">Updated</h3> */}
            <p className="mt-2 text-sm text-[#667085]">{successModal.message}</p>
            <button
              type="button"
              onClick={() => setSuccessModal((prev) => ({ ...prev, isOpen: false }))}
              className="mt-6 rounded-2xl bg-[#4f46e5] px-6 py-3 text-sm font-bold text-white transition hover:bg-[#4338ca]"
            >
              Okay
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalesHeadDashboard;
